import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { Clear, SearchGradient } from '#components/icons';
import styles from './SearchInput.module.scss';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';
import useTranslation from 'next-translate/useTranslation';

type InputProps = {
  onSubmit?: (value: string | undefined) => void | undefined;
  onChange?: (value: string) => void | undefined;
  value?: string | undefined;
  classes?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  loading?: boolean;
};

const SearchInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ value, onChange, onSubmit = null, classes, loading, inputProps }, ref) => {
    const [_value, _setValue] = useState(value);
    const { t } = useTranslation();

    const handleKeyEvents = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onSubmit?.(_value);
      }
    };
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      _setValue(event.target.value);
      onChange?.(event.target.value);
    };

    const handleClear = () => {
      _setValue('');
      onChange?.('');
    };

    //set internal value when value was changed from the outside
    useEffect(() => {
      _setValue(value);
    }, [value]);

    return (
      <div
        className={`${styles.host} tw-flex tw-items-center ${classes}`}
        role="presentation"
        data-testid="search-input"
      >
        <div className={styles.startAdornment}>
          {!loading && <SearchGradient classes="tw-mr-1.5" />}
          {loading && <CircularProgress size={20} color="#95e2ff" />}
        </div>
        <input
          ref={ref}
          className={styles.inputField}
          onChange={handleChange}
          onKeyDown={handleKeyEvents}
          placeholder={t('searchText')}
          type="search"
          autoComplete="off"
          value={_value}
          {...inputProps}
        />

        <div className="tw-flex tw-items-center tw-ml-4">
          {!!_value && (
            <button onClick={handleClear}>
              <Clear color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  }
);
SearchInput.displayName = 'SearchInput';

export default SearchInput;
